import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  FileText,
  Printer,
  Download,
  Calendar,
  Building2,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Users,
  Award,
  BookOpen,
  RefreshCw
} from 'lucide-react';

interface ExecutiveReportData {
  report_title: string;
  generated_at: string;
  academic_session: string;
  reporting_month: string;
  district_kpis: {
    total_schools: number;
    total_students: number;
    assessment_completion: number;
    average_score: number;
    low_performing_schools: number;
  };
  observations: string[];
  blocks_ranking: Array<{
    rank: number;
    block: string;
    score: number;
    completion: string;
  }>;
}

export const ExecutiveReportPage: React.FC = () => {
  const { fetchWithAuth } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState<string>('August');
  const [data, setData] = useState<ExecutiveReportData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const months = ['April', 'May', 'July', 'August', 'September', 'October', 'November', 'December', 'January', 'February'];

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`/api/reports/executive-summary?month=${selectedMonth}`).then(r => r.json());
      if (res.success && res.data) {
        setData(res.data);
      } else {
        setError(res.message || 'Failed to load executive report');
      }
    } catch (err: any) {
      setError(err.message || 'Error fetching report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [selectedMonth]);

  const handlePrint = () => {
    window.print();
  };

  const handleExportCSV = () => {
    if (!data) return;
    const rows = [
      ['District Education Office, Nuh — Executive Briefing'],
      ['Reporting Month', data.reporting_month],
      ['Academic Session', data.academic_session],
      ['Generated At', new Date(data.generated_at).toLocaleString('en-IN')],
      [],
      ['District Overview Metrics'],
      ['Total Government Schools', data.district_kpis.total_schools],
      ['Total Enrolled Students', data.district_kpis.total_students],
      ['Assessment Completion Rate (%)', data.district_kpis.assessment_completion],
      ['District Average Score (%)', data.district_kpis.average_score],
      ['Low Performing Schools (<40%)', data.district_kpis.low_performing_schools],
      [],
      ['Block Rankings & Performance'],
      ['Rank', 'Block Name', 'Average Score (%)', 'Submission Completion'],
      ...data.blocks_ranking.map(b => [b.rank, b.block, `${b.score}%`, b.completion]),
      [],
      ['Key Observations & Directives'],
      ...data.observations.map((obs, i) => [`${i + 1}`, obs])
    ];

    const csvContent = 'data:text/csv;charset=utf-8,' + rows.map(e => e.map(cell => `"${cell}"`).join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `DEO_Nuh_Executive_Report_${selectedMonth}_2026.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Top Action Bar (hidden in print) */}
      <div className="print:hidden flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-4 rounded-lg border border-slate-200 shadow-xs">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-50 border border-blue-200 rounded text-blue-900">
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-base font-bold text-slate-900">District Executive Review Briefing</h1>
            <p className="text-xs text-slate-500">Official monthly academic review report for District Administration</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center space-x-1.5 bg-slate-50 border border-slate-300 rounded px-2.5 py-1">
            <Calendar className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-xs font-semibold text-slate-700">Month:</span>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="bg-transparent text-xs font-bold text-slate-900 focus:outline-hidden cursor-pointer"
            >
              {months.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          <button
            onClick={fetchReport}
            className="flex items-center space-x-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded border border-slate-300 transition"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>

          <button
            onClick={handleExportCSV}
            disabled={!data}
            className="flex items-center space-x-1 px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold rounded transition shadow-xs disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Export CSV</span>
          </button>

          <button
            onClick={handlePrint}
            disabled={!data}
            className="flex items-center space-x-1 px-3 py-1.5 bg-[#0B1E38] hover:bg-[#15345e] text-white text-xs font-semibold rounded transition shadow-xs disabled:opacity-50"
          >
            <Printer className="w-3.5 h-3.5" />
            <span>Print Report / PDF</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white p-12 rounded-lg border border-slate-200 text-center">
          <div className="w-8 h-8 border-3 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-xs font-semibold text-slate-600">Generating Official District Briefing...</p>
        </div>
      ) : error ? (
        <div className="bg-rose-50 border border-rose-200 p-6 rounded-lg text-center">
          <AlertTriangle className="w-8 h-8 text-rose-600 mx-auto mb-2" />
          <p className="text-xs font-bold text-rose-900">{error}</p>
        </div>
      ) : data ? (
        /* Printable Official Government Document Container */
        <div className="bg-white p-8 sm:p-10 rounded-lg border border-slate-300 shadow-sm print:shadow-none print:border-none print:p-0 text-slate-900 space-y-8 font-sans">
          
          {/* Official Letterhead Header */}
          <div className="text-center pb-6 border-b-2 border-slate-900 space-y-1">
            <div className="text-sm font-bold tracking-widest text-slate-800 uppercase">
              Government of Haryana — Department of School Education
            </div>
            <div className="text-xl sm:text-2xl font-black text-[#0B1E38] tracking-tight uppercase">
              District Education Office, Nuh (Mewat)
            </div>
            <div className="text-xs font-bold text-amber-900 uppercase tracking-wider">
              Monthly Assessment & SAT Marks Monitoring Portal — Executive Review Briefing
            </div>
            <div className="flex justify-center items-center gap-4 text-[11px] text-slate-600 font-medium pt-2">
              <span><strong>Session:</strong> {data.academic_session}</span>
              <span>•</span>
              <span><strong>Reporting Month:</strong> {data.reporting_month} 2026</span>
              <span>•</span>
              <span><strong>Generated:</strong> {new Date(data.generated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            </div>
          </div>

          {/* Section 1: Executive KPI Matrix */}
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wider text-[#0B1E38] border-b border-slate-300 pb-1 mb-3">
              1. District Executive Summary
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="p-3 bg-slate-50 border border-slate-300 rounded text-center">
                <div className="text-[10px] font-bold text-slate-600 uppercase">Total Schools</div>
                <div className="text-xl font-black text-[#0B1E38] mt-1">{data.district_kpis.total_schools}</div>
                <div className="text-[9px] text-slate-500 font-medium">902 Govt Institutions</div>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-300 rounded text-center">
                <div className="text-[10px] font-bold text-slate-600 uppercase">Total Students</div>
                <div className="text-xl font-black text-blue-900 mt-1">{(data.district_kpis.total_students || 0).toLocaleString()}</div>
                <div className="text-[9px] text-slate-500 font-medium">Enrolled in Registry</div>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-300 rounded text-center">
                <div className="text-[10px] font-bold text-slate-600 uppercase">Assessment Compliance</div>
                <div className="text-xl font-black text-emerald-800 mt-1">{data.district_kpis.assessment_completion}%</div>
                <div className="text-[9px] text-slate-500 font-medium">Submitted on Portal</div>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-300 rounded text-center">
                <div className="text-[10px] font-bold text-slate-600 uppercase">District Avg Score</div>
                <div className="text-xl font-black text-indigo-900 mt-1">{data.district_kpis.average_score}%</div>
                <div className="text-[9px] text-slate-500 font-medium">Evaluated Assessments</div>
              </div>
              <div className="p-3 bg-rose-50 border border-rose-300 rounded text-center col-span-2 sm:col-span-1">
                <div className="text-[10px] font-bold text-rose-900 uppercase">Critical Schools</div>
                <div className="text-xl font-black text-rose-700 mt-1">{data.district_kpis.low_performing_schools}</div>
                <div className="text-[9px] text-rose-700 font-medium">Score &lt; 40%</div>
              </div>
            </div>
          </div>

          {/* Section 2: Block Performance Matrix */}
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wider text-[#0B1E38] border-b border-slate-300 pb-1 mb-3">
              2. Block Performance Ranking & Compliance
            </h2>
            <div className="overflow-x-auto border border-slate-300 rounded">
              <table className="w-full text-left text-xs">
                <thead className="bg-[#0B1E38] text-white font-semibold">
                  <tr>
                    <th className="py-2 px-3 text-center w-12">Rank</th>
                    <th className="py-2 px-3">Block Name</th>
                    <th className="py-2 px-3 text-right">Average Score (%)</th>
                    <th className="py-2 px-3 text-center">Submission Status</th>
                    <th className="py-2 px-3 text-center">Academic Band</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                  {data.blocks_ranking.map((block) => (
                    <tr key={block.block} className={block.rank === 1 ? 'bg-amber-50/50' : 'hover:bg-slate-50'}>
                      <td className="py-2.5 px-3 text-center font-bold">
                        <span className={`inline-block w-5 h-5 leading-5 rounded-full text-[10px] ${
                          block.rank === 1 ? 'bg-amber-400 text-slate-950' : 'bg-slate-200 text-slate-800'
                        }`}>
                          {block.rank}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 font-bold text-slate-900">{block.block}</td>
                      <td className="py-2.5 px-3 text-right font-black text-blue-900">{block.score.toFixed(1)}%</td>
                      <td className="py-2.5 px-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
                          block.completion === '100%' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                          {block.completion}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
                          block.score >= 60 ? 'bg-blue-100 text-blue-800' : 'bg-rose-100 text-rose-800'
                        }`}>
                          {block.score >= 60 ? 'Satisfactory (60-74%)' : 'Needs Improvement (40-59%)'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 3: Key Factual Observations */}
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wider text-[#0B1E38] border-b border-slate-300 pb-1 mb-3">
              3. Data-Driven Observations & Analysis
            </h2>
            <div className="space-y-2">
              {data.observations.map((obs, idx) => (
                <div key={idx} className="flex items-start space-x-2 text-xs text-slate-800 leading-relaxed">
                  <span className="font-bold text-[#0B1E38] min-w-[20px]">{idx + 1}.</span>
                  <span>{obs}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Section 4: Prescribed Action Directives */}
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wider text-[#0B1E38] border-b border-slate-300 pb-1 mb-3">
              4. Recommended Administrative Directives
            </h2>
            <div className="bg-slate-50 border border-slate-300 p-4 rounded space-y-2 text-xs text-slate-800 leading-relaxed">
              <p>
                <strong>A. Mandatory Remedial Intervention:</strong> Schools with composite scores below 40% in Block Ferozepur Jhirka and Block Nagina must institute 45-minute daily zero-period remedial classes focusing on Mathematics foundational concepts.
              </p>
              <p>
                <strong>B. 100% Submission Adherence:</strong> Block Education Officers of Punhana, Nagina, and Ferozepur Jhirka are directed to ensure all non-submitted schools finalize their Monthly Assessment data entries within 48 hours.
              </p>
              <p>
                <strong>C. Class 10 & 12 Board Readiness:</strong> Special test series and past-year question paper reviews must be accelerated for senior secondary batches to consolidate the 7.2% upward learning trajectory observed district-wide.
              </p>
            </div>
          </div>

          {/* Section 5: Official Signature Block */}
          <div className="pt-12 grid grid-cols-2 gap-8 text-center text-xs">
            <div>
              <div className="h-10"></div>
              <div className="border-t border-slate-400 pt-1">
                <div className="font-bold text-slate-900">District Project Coordinator</div>
                <div className="text-[10px] text-slate-600">Samagra Shiksha Abhiyan, District Nuh</div>
              </div>
            </div>
            <div>
              <div className="h-10"></div>
              <div className="border-t border-slate-400 pt-1">
                <div className="font-bold text-slate-900">District Education Officer</div>
                <div className="text-[10px] text-slate-600">Department of School Education, District Nuh (Haryana)</div>
              </div>
            </div>
          </div>

          {/* Footer Notice */}
          <div className="pt-4 border-t border-slate-200 text-center text-[10px] text-slate-500">
            This document is computer-generated from the official Monthly Assessment and SAT Marks Monitoring Portal, District Education Office, Nuh.
          </div>
        </div>
      ) : null}
    </div>
  );
};
